import NinjaTraderTrademarkNotice from "./NinjaTraderTrademarkNotice";

type RiskDisclosureProps = {
  /**
   * When true, appends the testimonials disclaimer in the SAME section, below
   * the disclosures, with no divider between them. Landing page only.
   */
  showTestimonialsDisclaimer?: boolean;
  /**
   * When true, appends the NinjaTrader trademark notice. Required on any page
   * that mentions NinjaTrader; the landing page does (SocialProofBar logo).
   */
  showNinjaTraderNotice?: boolean;
};

/**
 * NinjaTrader Vendor Program — footer disclosures.
 *
 * Rendered once at the App root (see AppContent in src/client/App.tsx) on
 * public-facing routes only: the landing page, marketing/SEO pages, blog,
 * legal, auth and session-share pages. It is intentionally hidden inside the
 * authenticated app UI (dashboard, journal, admin, …).
 *
 * Two statements are mandatory in the footer of every page: the Futures Risk
 * Disclosure and the CFTC Hypothetical Performance Disclosure. Both are English
 * verbatim per the Vendor Compliance Guidelines and must stay English in both
 * FR and EN UI — do NOT route them through i18n. The optional testimonials
 * disclaimer (landing only) lives in the same bordered section so the
 * statements read as one block without a divider between them.
 */
export default function RiskDisclosure({
  showTestimonialsDisclaimer = false,
  showNinjaTraderNotice = false,
}: RiskDisclosureProps) {
  return (
    <footer className="border-t border-border bg-background">
      <div className="mx-auto max-w-4xl space-y-3 px-4 py-4 text-center text-xs leading-relaxed text-foreground/80">
        <p>
          Futures and forex trading contains substantial risk and is not for
          every investor. An investor could potentially lose all or more than the
          initial investment. Risk capital is money that can be lost without
          jeopardizing one's financial security or life style. Only risk capital
          should be used for trading and only those with sufficient risk capital
          should consider trading. Past performance is not necessarily indicative
          of future results.
        </p>
        <p>
          Hypothetical performance results have many inherent limitations, some
          of which are described below. No representation is being made that any
          account will or is likely to achieve profits or losses similar to those
          shown; in fact, there are frequently sharp differences between
          hypothetical performance results and the actual results subsequently
          achieved by any particular trading program. One of the limitations of
          hypothetical performance results is that they are generally prepared
          with the benefit of hindsight. In addition, hypothetical trading does
          not involve financial risk, and no hypothetical trading record can
          completely account for the impact of financial risk of actual trading.
          For example, the ability to withstand losses or to adhere to a
          particular trading program in spite of trading losses are material
          points which can also adversely affect actual trading results. There
          are numerous other factors related to the markets in general or to the
          implementation of any specific trading program which cannot be fully
          accounted for in the preparation of hypothetical performance results
          and all which can adversely affect trading results.
        </p>
        {showTestimonialsDisclaimer && (
          <p>
            Testimonials appearing on this website may not be representative of
            other clients or customers and is not a guarantee of future
            performance or success.
          </p>
        )}
        {showNinjaTraderNotice && <NinjaTraderTrademarkNotice variant="plain" />}
      </div>
    </footer>
  );
}
